import { useState, useRef, useEffect } from "react";
import { Search, Moon, Sun, Bell, Sparkles, ChevronDown, User, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth-context";
import { useTheme } from "../lib/theme";
import { useToast } from "../lib/toast";

export function Topbar({ title, subtitle, onOpenPalette, onOpenChat }: { title: string; subtitle: string; onOpenPalette: () => void; onOpenChat: () => void }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <>
      <header className="h-16 shrink-0 border-b border-slate-200 dark:border-white/10 bg-white/80 dark:bg-surface-1/80 backdrop-blur flex items-center gap-3 px-4">
        <button onClick={onOpenPalette} className="flex items-center gap-2 text-sm text-slate-400 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-1.5 w-72 max-w-[40vw] hover:border-slate-300">
          <Search className="w-4 h-4" /> Search or jump to…
          <kbd className="ml-auto text-[10px] border rounded px-1 border-slate-300 dark:border-white/20">⌘K</kbd>
        </button>
        <div className="ml-auto flex items-center gap-2">
          <select className="text-xs rounded-lg border border-slate-200 dark:border-white/10 bg-transparent px-2 py-1.5">
            <option>EN</option>
            <option>中文</option>
            <option>PT-BR</option>
          </select>
          <button onClick={toggle} className="h-9 w-9 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 flex items-center justify-center">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button onClick={() => toast("No new notifications")} className="relative h-9 w-9 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 flex items-center justify-center">
            <Bell className="w-4 h-4" />
          </button>
          <button onClick={onOpenChat} className="flex items-center gap-1.5 text-xs font-semibold rounded-lg bg-sbd-black text-white dark:bg-sbd-yellow dark:text-sbd-black px-3 py-2 hover:opacity-90">
            <Sparkles className="w-3.5 h-3.5" /> Ask GSP
          </button>
          <div className="h-8 w-px bg-slate-200 dark:bg-white/10" />
          <div className="relative" ref={menuRef}>
            <button onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-white/5">
              <div className="h-7 w-7 rounded-full bg-sbd-yellow text-sbd-black font-bold text-xs flex items-center justify-center">{user?.initials}</div>
              <div className="text-left hidden sm:block">
                <div className="text-xs font-semibold leading-none">{user?.fullName}</div>
                <div className="text-[11px] text-slate-400">{user?.roles[0]}</div>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-surface-3 border border-slate-200 dark:border-white/10 rounded-xl shadow-2xl py-1.5 z-[70] text-sm">
                <div className="px-3 py-2 border-b border-slate-100 dark:border-white/10">
                  <div className="font-semibold text-sm">{user?.fullName}</div>
                  <div className="text-xs text-slate-400 truncate">{user?.email}</div>
                </div>
                <button onClick={() => toast("Profile settings")} className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-white/5 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" /> View Profile
                </button>
                <div className="h-px bg-slate-100 dark:bg-white/10 my-1" />
                <button onClick={() => logout()} className="w-full text-left px-3 py-2 hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center gap-2">
                  <LogOut className="w-4 h-4" /> Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="px-6 pt-5 pb-3 flex items-center gap-3">
        <h1 className="text-xl font-bold">{title}</h1>
        <span className="text-sm text-slate-400">{subtitle}</span>
      </div>
    </>
  );
}
